/**
 * KDP Publishing Walkthrough
 * Implements the vertical checklist + book-preview sidebar layout from:
 * DennisAutomations/storymint/ux/kdp-walkthrough.html
 *
 * Route: /kdp-walkthrough?bookId=<optional>
 * Free tier: steps 1–2 accessible, steps 3–5 locked behind upgrade overlay
 *
 * When no bookId is present in the URL, the sidebar shows a book-selection
 * dropdown so the user can pick any of their books inline.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { useBook, useBooks } from '../hooks/useBooks';
import { useCovers } from '../services/covers';
import { useExportBook } from '../services/export';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';

// ── Step definitions ──────────────────────────────────────────────────────────

interface ChecklistItem {
  label: string;
}

interface KdpStep {
  key: string;
  icon: string;
  title: string;
  body: string;
  items: ChecklistItem[];
  freeAccess: boolean;
}

const KDP_STEPS: KdpStep[] = [
  {
    key: 'accountSetup',
    icon: '👤',
    title: 'Create your KDP account',
    body: 'Go to kdp.amazon.com and sign in with your Amazon account (or create a new one). Complete tax information and add your bank account for royalty payments.',
    items: [
      { label: 'Visit kdp.amazon.com' },
      { label: 'Complete tax interview (W-9 or W-8BEN)' },
      { label: 'Add bank account for royalties' },
    ],
    freeAccess: true,
  },
  {
    key: 'bookMetadata',
    icon: '📝',
    title: 'Add book metadata',
    body: 'From your KDP Bookshelf, click + Kindle eBook. Fill in title, author name, description, 2 BISAC categories, and 7 keywords.',
    items: [
      { label: 'Title and author name' },
      { label: 'Book description (100–200 words)' },
      { label: '2 categories + 7 keywords' },
    ],
    freeAccess: true,
  },
  {
    key: 'coverUpload',
    icon: '🎨',
    title: 'Upload your cover',
    body: 'Export your cover from StoryMint and upload it to KDP. KDP requires at minimum 1000 × 1600 px, JPG or TIFF, sRGB color space.',
    items: [
      { label: 'Export cover from StoryMint (Dashboard → Book → Cover → Download)' },
      { label: 'Upload to KDP Content tab' },
      { label: 'Preview in KDP cover viewer — check thumbnail readability' },
    ],
    freeAccess: false,
  },
  {
    key: 'pricing',
    icon: '💰',
    title: 'Set pricing & royalty plan',
    body: 'Choose your price and royalty option. The 70% royalty applies to books priced $2.99–$9.99 — recommended for most indie authors. Start at $2.99 to build reviews fast.',
    items: [
      { label: 'Select All Territories' },
      { label: 'Choose royalty plan (35% or 70%)' },
      { label: 'Set list price ($2.99–$4.99 recommended for debut)' },
      { label: 'Optionally enroll in KDP Select (Kindle Unlimited)' },
    ],
    freeAccess: false,
  },
  {
    key: 'publish',
    icon: '🚀',
    title: 'Submit for review & publish',
    body: 'Click Publish Your Kindle eBook. Amazon review takes 24–72 hours. You\'ll get an email when your book is live.',
    items: [
      { label: 'Review all details one final time' },
      { label: 'Click "Publish Your Kindle eBook"' },
      { label: 'Wait for Amazon review (24–72h)' },
      { label: 'Share your Amazon listing 🎉' },
    ],
    freeAccess: false,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

type StepStatus = 'complete' | 'active' | 'pending' | 'locked';

function getStepStatus(
  idx: number,
  checkedItems: Record<string, boolean>,
  isFreeTier: boolean,
): StepStatus {
  const step = KDP_STEPS[idx]!;
  if (isFreeTier && !step.freeAccess) return 'locked';
  const allChecked = step.items.every((_, i) => checkedItems[`${idx}-${i}`]);
  if (allChecked) return 'complete';
  // First non-complete unlocked step is "active"
  for (let i = 0; i < idx; i++) {
    const s = KDP_STEPS[i]!;
    if (isFreeTier && !s.freeAccess) continue;
    const prevComplete = s.items.every((_, j) => checkedItems[`${i}-${j}`]);
    if (!prevComplete) return 'pending';
  }
  return 'active';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StepCircle({ status, icon }: { status: StepStatus; icon: string }) {
  if (status === 'complete') {
    return (
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-green-500 bg-green-500/15 text-green-400 shrink-0">
        ✓
      </div>
    );
  }
  if (status === 'active') {
    return (
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-primary bg-primary/15 text-primary shrink-0">
        {icon}
      </div>
    );
  }
  if (status === 'locked') {
    return (
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-border bg-card text-muted-foreground shrink-0 opacity-40">
        {icon}
      </div>
    );
  }
  // pending
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-border bg-card text-muted-foreground shrink-0">
      {icon}
    </div>
  );
}

function StatusBadge({ status }: { status: StepStatus }) {
  if (status === 'complete') {
    return (
      <span className="text-xs px-2.5 py-0.5 rounded-full bg-green-500/15 text-green-400 font-medium shrink-0">
        Done
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary/15 text-[#a78bfa] font-medium shrink-0">
        Up next
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="text-xs px-2.5 py-0.5 rounded-full bg-white/5 text-muted-foreground font-medium shrink-0">
        Locked
      </span>
    );
  }
  return (
    <span className="text-xs px-2.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 font-medium shrink-0">
      Pending
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function KdpWalkthrough() {
  const { isAuthenticated } = useAuth();
  const { data: profile } = useUserProfile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlBookId = searchParams.get('bookId') ?? undefined;

  // When no bookId in URL, the user picks from a dropdown; track that choice locally.
  const [selectedBookId, setSelectedBookId] = useState<string>('');

  // The resolved ID used for data fetching and exports: URL param wins, then dropdown.
  const resolvedBookId = urlBookId ?? (selectedBookId || undefined);

  // Only fetch the full books list when there is no URL bookId (selector mode).
  const { data: books, isLoading: booksLoading } = useBooks({ enabled: !urlBookId });
  const { data: book } = useBook(resolvedBookId);
  const { data: covers, isLoading: coversLoading } = useCovers(resolvedBookId ?? '');
  const exportMutation = useExportBook(resolvedBookId ?? '');

  const primaryCoverUrl = covers && covers.length > 0
    ? (covers.find((c) => c.id === book?.coverSelectedId) ?? covers[0])!.publicUrl
    : null;

  // FIX QA-19 MEDIUM-2: Use subscription tier for free-tier check.
  // `lifetimeCreditsPurchased` is never initialized in Firestore on user creation
  // so `credits.lifetimeCreditsPurchased === 0` is always false for paid users
  // who haven't bought a credit pack. Subscription tier is the reliable gate.
  const isFreeTier = !profile || profile.subscriptionTier === 'free' || profile.subscriptionTier === null;

  // Per-item checked state: key = "${stepIdx}-${itemIdx}"
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const toggleItem = (stepIdx: number, itemIdx: number) => {
    const key = `${stepIdx}-${itemIdx}`;
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async (format: 'epub' | 'docx') => {
    if (!resolvedBookId) return;
    try {
      const data = await exportMutation.mutateAsync(format);
      window.open(data.downloadUrl, '_blank');
    } catch {
      alert('Export failed. Please try again.');
    }
  };

  // Whether we are in the "no URL context" mode and need the selector
  const needsBookSelection = !urlBookId;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar variant={isAuthenticated ? 'authed' : 'public'} />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">

        {/* Page header */}
        <div className="mb-8 sm:mb-10">
          <Link
            to="/dashboard"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 mb-4"
          >
            ← Back to My Books
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Publish to Amazon KDP
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
            Follow these steps to get your finished book live on Amazon Kindle.
            {isFreeTier
              ? ' Upgrade to access the full walkthrough.'
              : ' Takes about 15 minutes the first time.'}
          </p>
          {isFreeTier && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-900/20 border border-amber-700/30 rounded-full text-amber-400 text-xs font-medium">
              ⚠️ Steps 1–2 free · Upgrade for full access
            </div>
          )}
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">

          {/* ── LEFT: Step checklist ── */}
          <div className="space-y-0">
            {KDP_STEPS.map((step, idx) => {
              const status = getStepStatus(idx, checkedItems, isFreeTier);
              const isLast = idx === KDP_STEPS.length - 1;

              return (
                <div key={step.key} className="flex items-start gap-4">

                  {/* Circle + connector line */}
                  <div className="flex flex-col items-center shrink-0">
                    <StepCircle status={status} icon={step.icon} />
                    {!isLast && (
                      <div className={`w-0.5 flex-1 min-h-[24px] mt-1 ${
                        status === 'complete' ? 'bg-green-500/35' : 'bg-border'
                      }`} />
                    )}
                  </div>

                  {/* Step card */}
                  <div className={`flex-1 pb-4 ${isLast ? '' : ''}`}>
                    <div className={`border rounded-xl bg-card p-5 sm:p-6 relative overflow-hidden ${
                      status === 'active' ? 'border-primary/35' : 'border-border'
                    } ${status === 'locked' ? 'min-h-[120px]' : ''}`}>

                      {/* Card header */}
                      <div className={`flex items-center gap-3 mb-3 ${status === 'locked' ? 'opacity-30' : ''}`}>
                        <span className="text-xs font-bold tracking-widest uppercase text-primary">
                          Step {idx + 1}
                        </span>
                        <h2 className="text-base font-semibold text-foreground flex-1">
                          {step.title}
                        </h2>
                        <StatusBadge status={status} />
                      </div>

                      {/* Card body — hidden under overlay when locked */}
                      {status !== 'locked' && (
                        <div className="text-sm text-muted-foreground leading-relaxed">
                          <p className="mb-3">{step.body}</p>
                          <ul className="space-y-2">
                            {step.items.map((item, iIdx) => {
                              const checked = !!checkedItems[`${idx}-${iIdx}`];
                              return (
                                <li
                                  key={iIdx}
                                  className="flex items-start gap-2 cursor-pointer select-none group"
                                  onClick={() => toggleItem(idx, iIdx)}
                                >
                                  <span className={`mt-0.5 shrink-0 text-base leading-none transition-colors ${
                                    checked ? 'text-green-400' : 'text-muted-foreground group-hover:text-foreground'
                                  }`}>
                                    {checked ? '✓' : '○'}
                                  </span>
                                  <span className={checked ? 'line-through opacity-60' : ''}>
                                    {item.label}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Locked overlay — glassmorphism blur */}
                      {status === 'locked' && (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6 py-4 rounded-xl"
                          style={{ background: 'rgba(15,15,20,0.72)', backdropFilter: 'blur(2px)' }}
                        >
                          <span className="text-2xl">🔒</span>
                          <p className="text-sm text-muted-foreground">
                            {step.key === 'coverUpload' && 'Cover upload guide available on paid plans.'}
                            {step.key === 'pricing' && 'Pricing strategy guide available on paid plans.'}
                            {step.key === 'publish' && 'Publishing checklist available on paid plans.'}
                          </p>
                          <button
                            onClick={() => void navigate('/pricing')}
                            className="text-sm font-semibold text-[#a78bfa] hover:text-foreground transition-colors"
                          >
                            Upgrade to unlock →
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              );
            })}
          </div>

          {/* ── RIGHT: Sidebar ── */}
          <div className="flex flex-col gap-5">

            {/* Book preview card */}
            <div className="border border-border rounded-xl bg-card p-6">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">
                Your Book
              </h3>

              {/* ── Book selector (shown only when no URL bookId) ── */}
              {needsBookSelection && (
                <div className="mb-4">
                  {booksLoading ? (
                    <div className="h-9 rounded-lg bg-muted/40 animate-pulse" />
                  ) : !books || books.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-4 text-center">
                      <p className="text-xs text-muted-foreground">
                        You don't have any books yet.
                      </p>
                      <Link
                        to="/books/new"
                        className="px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-md hover:bg-primary/90 transition-colors"
                      >
                        Create Your First Book
                      </Link>
                    </div>
                  ) : (
                    <select
                      value={selectedBookId}
                      onChange={(e) => setSelectedBookId(e.target.value)}
                      className="w-full rounded-lg border border-border bg-background text-foreground text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
                    >
                      <option value="">Select a book…</option>
                      {books.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Cover thumbnail */}
              <div
                className="w-full rounded-xl border border-border mb-4 flex flex-col items-center justify-end relative overflow-hidden"
                style={{ aspectRatio: '6/9', background: 'linear-gradient(145deg, #1e1e3f 0%, #0f0f14 100%)' }}
              >
                {primaryCoverUrl ? (
                  /* Actual cover image */
                  <img
                    src={primaryCoverUrl}
                    alt={book?.title ?? 'Book cover'}
                    className="absolute inset-0 w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <>
                    {/* Subtle diagonal pattern placeholder */}
                    <div
                      className="absolute inset-0 rounded-xl"
                      style={{
                        backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 12px, rgba(124,58,237,0.04) 12px, rgba(124,58,237,0.04) 13px)',
                      }}
                    />
                    <div className="relative text-center p-4">
                      <p className="text-sm font-bold text-foreground text-shadow">
                        {book?.title ?? 'Your Book Title'}
                      </p>
                      <p className="text-xs text-white/60 mt-1">
                        {book ? 'by Author' : ''}
                      </p>
                      {/* Create Cover CTA — only shown when covers have loaded and none exist */}
                      {resolvedBookId && !coversLoading && (!covers || covers.length === 0) && (
                        <Link
                          to={`/books/${resolvedBookId}/covers`}
                          className="mt-3 inline-block px-3 py-1.5 bg-primary/80 hover:bg-primary text-primary-foreground text-xs font-semibold rounded-md transition-colors"
                        >
                          Create Cover
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Book meta */}
              <div className="space-y-2">
                {book ? (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Genre</span>
                      <span className="font-medium capitalize">{book.genre}</span>
                    </div>
                    {book.premise && (
                      <div className="text-xs">
                        <span className="text-muted-foreground block mb-1">Description</span>
                        <p className="text-foreground/80 leading-relaxed line-clamp-3">{book.premise}</p>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Word count</span>
                      <span className="font-medium">{book.wordCount.toLocaleString()} words</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Chapters</span>
                      <span className="font-medium">{book.chaptersTotal} chapters</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Status</span>
                      <span className={`font-medium ${book.status === 'complete' ? 'text-green-400' : 'text-yellow-400'}`}>
                        {book.status === 'complete' ? '✓ Complete' : book.status}
                      </span>
                    </div>
                  </>
                ) : (
                  !needsBookSelection && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Open from a specific book to see details here.
                    </p>
                  )
                )}
              </div>
            </div>

            {/* Download card (paid only) */}
            {!isFreeTier && (
              <div className="border border-border rounded-xl bg-card p-5 sm:p-6">
                <h3 className="text-sm font-semibold text-foreground mb-4">
                  Download Your Manuscript
                </h3>

                {/* EPUB row */}
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📘</span>
                    <div>
                      <p className="text-sm font-medium text-foreground">EPUB</p>
                      <p className="text-xs text-muted-foreground">For Kindle &amp; eReaders</p>
                    </div>
                  </div>
                  <button
                    onClick={() => void handleExport('epub')}
                    disabled={!resolvedBookId || exportMutation.isPending}
                    className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {exportMutation.isPending ? '…' : 'Download'}
                  </button>
                </div>

                {/* DOCX row */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📄</span>
                    <div>
                      <p className="text-sm font-medium text-foreground">DOCX</p>
                      <p className="text-xs text-muted-foreground">Print-ready manuscript</p>
                    </div>
                  </div>
                  <button
                    onClick={() => void handleExport('docx')}
                    disabled={!resolvedBookId || exportMutation.isPending}
                    className="px-3 py-1.5 border border-border text-foreground text-xs font-semibold rounded-md hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    {exportMutation.isPending ? '…' : 'Download'}
                  </button>
                </div>
              </div>
            )}

            {/* KDP CTA / Upgrade CTA */}
            {isFreeTier ? (
              /* Free tier: upgrade CTA */
              <div
                className="rounded-xl p-5 sm:p-6 text-center border"
                style={{
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(124,58,237,0.04) 100%)',
                  borderColor: 'rgba(124,58,237,0.25)',
                }}
              >
                <div className="text-3xl mb-2">🔓</div>
                <h3 className="text-base font-semibold text-foreground mb-2">
                  Unlock full KDP walkthrough
                </h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  Download your manuscript, get step-by-step cover and pricing guidance, and publish with confidence.
                </p>
                <Link
                  to="/pricing"
                  className="block w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-md hover:bg-primary/90 transition-colors text-center"
                >
                  View Plans &amp; Upgrade →
                </Link>
                <p className="text-xs text-muted-foreground mt-3">Starting at $19/month · Cancel anytime</p>
              </div>
            ) : (
              /* Paid tier: Open KDP CTA */
              <div
                className="rounded-xl p-5 sm:p-6 text-center border"
                style={{
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(124,58,237,0.04) 100%)',
                  borderColor: 'rgba(124,58,237,0.25)',
                }}
              >
                <div className="text-3xl mb-2">📦</div>
                <h3 className="text-base font-semibold text-foreground mb-2">Open Amazon KDP</h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  Ready to upload? Open KDP in a new tab and follow the steps on the left side by side.
                </p>
                <a
                  href="https://kdp.amazon.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-md hover:bg-primary/90 transition-colors text-center"
                >
                  Open KDP Dashboard →
                </a>
                <p className="text-xs text-muted-foreground mt-3">You&apos;ll need your Amazon account credentials</p>
              </div>
            )}

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
